import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router'
import { Injectable, OnDestroy } from '@angular/core';

import { forkJoin, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { BaseRestService } from './base-rest.service';
import { IStatisticList, Statistic } from '../models/statistic';
import { UserService } from './user.service';
import { EventService } from './event.service';
import { SubjectSettingService } from './subject-setting.service';
import { DateHelper } from 'app/main/content/misc/date.module';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Injectable({ providedIn: 'root' })
export class StatisticsService extends BaseRestService<Statistic, IStatisticList> implements OnDestroy {

  subjectSnapshotStats: Statistic[] = [];
  referralSnapshotStats: Statistic[] = [];

  subjectsDueReviewSnapshotStats: Statistic[] = [];
  subjectsDueReviewChanged = true;

  subjectsDueReviewSnapshotStatsNew: SubjectsDueReviewDTO[] = [];//need to hook this up to the stat calls so that its what the data gets stuffed into rather than a stat

  subjectStats: Statistic[] = [];
  taskStats: Statistic[] = [];
  taskSnapshotStats: Statistic[] = [];
  tasksChanged = true;
  subjectsChanged = true;
  referralsChanged = true;

  private onUpdateTaskStats$: Subscription;
  private onUpdateSubjectStats$: Subscription;
  private onUpdateReferralStats$: Subscription;

  private onUpdateSubjectsDueReviewStats$: Subscription;

  onStatisticsUpdated: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(http: HttpClient, private userService: UserService, private eventService: EventService, protected router: Router, private subjectSettingService: SubjectSettingService, private dateHelper: DateHelper) {
    super(StatisticsService, http, 'api/statistics', 'statistics', router);
    this.cacheEnabled = false;

    this.onUpdateTaskStats$ = this.eventService.onUpdateTaskStats.subscribe((_) => {
      // need this to update the task badges on the navbar
      this.getUserTaskStats(this.userService.getCurrentUserUid());

      // set the tasksChanged flag - this ensures task statistics are re-fetched on display of the home dashboard
      this.tasksChanged = true;
    });

    this.onUpdateSubjectStats$ = this.eventService.onUpdateSubjectStats.subscribe((_) => {
      // set flag to re-fetch subject and task statistics on display of home dashboard
      this.subjectsChanged = true;
      this.getAllStatistics();
    });

    this.onUpdateReferralStats$ = this.eventService.onUpdateReferralStats.subscribe((_) => {
      // set flag to re-fetch subject and task statistics on display of home dashboard      
      this.referralsChanged = true;
      this.getAllStatistics();
    });

    this.onUpdateSubjectsDueReviewStats$ = this.eventService.onUpdateSubjectsDueReviewStats.subscribe((_) => {
      this.subjectsDueReviewChanged = true;
      this.getAllStatistics();
    })

  }

  ngOnDestroy() {
    this.onUpdateTaskStats$.unsubscribe();
    this.onUpdateSubjectStats$.unsubscribe();
    this.onUpdateReferralStats$.unsubscribe();
    this.onUpdateSubjectsDueReviewStats$.unsubscribe();
    //
  }

  // get all available statistics for both subjects and tasks for within the (optional) date range specified
  getAllStatistics(from?: string, to?: string): Observable<boolean> {
    const stats$ = [];
    stats$.push(of(null));

    if (this.subjectsChanged) {
      stats$.push(this.getAllSubjectSnapshotStatistics());
      stats$.push(this.getAllSubjectStatistics(from, to));
    }
    if (this.subjectsChanged || this.tasksChanged) {
      stats$.push(this.getAllTaskSnapshotStatistics());
      stats$.push(this.getAllTaskStatistics(from, to));
    }
    if (this.referralsChanged) {
      stats$.push(this.getAllReferralSnapshotStatistics());
    }
    if (this.subjectsDueReviewChanged) {
      stats$.push(this.getAllSubjectsDueReviewSnapshotStatistics());
    }

    return forkJoin(stats$).pipe(take(1), map((_) => {
      // console.log('Stats retrieved');
      this.onStatisticsUpdated.next(true);
      this.tasksChanged = false;
      this.subjectsChanged = false;
      this.referralsChanged = false;
      this.subjectsDueReviewChanged = false;
      return true;
    }));
  }

  getAllSubjectsDueReviewSnapshotStatistics(): Observable<any> {
    const getUrl = `${this.resourceUrl}/subjectsduereviewsnapshot`;
    return this.testGetSubjectsDueReview(getUrl).pipe(map((response) => {
      if (response) {
        this.subjectsDueReviewSnapshotStatsNew = response;
      }
    }));
  }

  // get all subject snapshot statistics
  getAllSubjectSnapshotStatistics(): Observable<any> {
    const getUrl = `${this.resourceUrl}/subjectsnapshot`;
    return this.fetchStatistics(getUrl).pipe(map((response) => {
      if (response) {
        this.subjectSnapshotStats = response;
      }
    }));
  }

  getAllReferralSnapshotStatistics(): Observable<any> {
    const getUrl = `${this.resourceUrl}/referralssnapshot`;
    return this.fetchStatistics(getUrl).pipe(map((response) => {
      if (response) {
        this.referralSnapshotStats = response;
      }
    }));
  }

  getAllSubjectStatistics(from?: string, to?: string): Observable<any> {
    // const fromDate = this.getFromDate(from);
    // const getUrl = `${this.resourceUrl}/subject/${fromDate}/${this.getToDate(fromDate, to)}`;
    // return this.fetchStatistics(getUrl);
    return of(null);
  }

  getAllReferralStatistics(from?: string, to?: string): Observable<any> {
    // const fromDate = this.getFromDate(from);
    // const getUrl = `${this.resourceUrl}/subject/${fromDate}/${this.getToDate(fromDate, to)}`;
    // return this.fetchStatistics(getUrl);
    return of(null);
  }

  getAllTaskSnapshotStatistics(): Observable<any> {
    const getUrl = `${this.resourceUrl}/tasksnapshot`;
    return this.fetchStatistics(getUrl).pipe(map((response) => {
      if (response) {
        this.taskSnapshotStats = response;
      }
    }));
  }

  getAllTaskStatistics(from?: string, to?: string): Observable<any> {
    const fromDate = this.getFromDate(from);
    const getUrl = `${this.resourceUrl}/task/${fromDate}/${this.getToDate(fromDate, to)}`;
    return this.fetchStatistics(getUrl).pipe(map((response) => {
      if (response) {
        this.taskStats = response;
      }
    }));
  }

  // if date not specified then
  private getFromDate(from?: string) {
    from = from || dayjs.utc().subtract(1, 'months').format('DD/MMM/YYYY');
    return this.dateHelper.getDateAsString(from, 'YYYY-MM-DD');
  }

  private getToDate(from: string, to?: string) {
    to = to || dayjs.utc().add(1, 'days').format('DD/MMM/YYYY');
    let toMoment = this.dateHelper.utc(to);
    const fromMoment = this.dateHelper.utc(from, 'YYYY-MM-DD');
    if (toMoment < fromMoment) {
      toMoment = this.dateHelper.utc(fromMoment);
    }
    return this.dateHelper.getDateAsString(to, 'YYYY-MM-DD');
  }

  private fetchStatistics(url: string): Observable<Statistic[]> {
    let stats: Statistic[] = [];
    if (url) {
      return this.http.get<Statistic[]>(url).pipe(take(1), map((response) => {
        if (response) {
          stats = response;
        }
        return stats;
      }));
    }
    else {
      return of(stats);
    }
  }
  private testGetSubjectsDueReview(url: string): Observable<SubjectsDueReviewDTO[]> {
    let stats: SubjectsDueReviewDTO[] = [];
    if (url) {
      return this.http.get<SubjectsDueReviewDTO[]>(url).pipe(map((response) => {
        if (response) {
          stats = response;
        }
        return stats;
      }));
    }
    else {
      return of(stats);
    }
  }

  getSubjectSnapshotStat(
    subjectTypeId: number,
    isReferral: boolean = false,
    stateId?: number,
    status?: number[],
    category?: number[],
    caseType?: number[],
    organisationId?: number,
    users?: number[],
    context?: number[],
  ): number {
    if (this.subjectSnapshotStats.length > 0) {
      const typePredicate = (x) => x.typeId === subjectTypeId;
      const isReferralPredicate = (x) => x.isReferral === isReferral;
      const statePredicate = (x) => x.stateId === stateId;
      const statusPredicate = (x) => status.includes(x.statusId);
      const statusPredicateIncludeBlank = (x) => (status.includes(x.statusId)) || (!x.statusId);

      const categoryPredicate = (x) => category.includes(x.categoryId);
      const categoryPredicateIncludeBlank = (x) => (category.includes(x.categoryId)) || (!x.categoryId);

      const caseTypePredicate = (x) => caseType.includes(x.caseTypeId);
      const caseTypePredicateIncludeBlank = (x) => (caseType.includes(x.caseTypeId)) || (!x.caseTypeId);

      const organisationPredicate = (x) => x.organisationId === organisationId;

      // if no users specified, set to empty array
      users = users || [];

      let predicate = (x) => typePredicate(x) && isReferralPredicate(x);

      // get context predicate
      let contextPredicate;
      if (context && (context.length > 0)) {
        switch (context[0]) {
          case -2: // manager
            contextPredicate = (x) => users.includes(x.managerId);
            break;
          case -1: // filter by user
            contextPredicate = (x) => users.includes(x.userId);
            break;
          case 0:
            // do nothing
            break;
          default:
            contextPredicate = (x) => context.includes(x.userId);
            break;
        }
      }

      if (contextPredicate) {
        const prev = predicate;
        predicate = (x) => prev(x) && contextPredicate(x);
      }

      // if stateId specified then use it to filter list...
      if (stateId && (stateId > 0)) {
        const prev = predicate;
        predicate = (x) => prev(x) && statePredicate(x);
      }

      // if status specified then use it to filter list...
      if (status && (status.length > 0)) {
        const prev = predicate;
        if (status.includes(-1)) {
          predicate = (x) => prev(x) && statusPredicateIncludeBlank(x);
        }
        else {
          predicate = (x) => prev(x) && statusPredicate(x);
        }
      }

      // if category specified then use it to filter list...
      if (category && (category.length > 0)) {
        const prev = predicate;
        if (category.includes(-1)) {
          predicate = (x) => prev(x) && categoryPredicateIncludeBlank(x);
        }
        else {
          predicate = (x) => prev(x) && categoryPredicate(x);
        }
      }

      // if caseType specified then use it to filter list...
      if (caseType && (caseType.length > 0)) {
        const prev = predicate;
        if (caseType.includes(-1)) {
          predicate = (x) => prev(x) && caseTypePredicateIncludeBlank(x);
        }
        else {
          predicate = (x) => prev(x) && caseTypePredicate(x);
        }
      }

      // if organisationId specified then use it to filter list...
      if (organisationId && (organisationId > 0)) {
        const prev = predicate;
        predicate = (x) => prev(x) && organisationPredicate(x);
      }

      // now we have a filtered list of statistics to include, sum the counts to give us the value to return
      // + operator for casting to Number
      return this.subjectSnapshotStats.filter((x) => predicate(x)).reduce((a, b) => +a + +b.count, 0);
    }
    else {
      return this.noStat();
    }
  }

  getSubjectStat(subjectTypeId: number, from: string, to: string, stateId?: number, users?: number[], context?: string) {
    if (this.subjectStats.length > 0) {

    }
    else {
      return this.noStat();
    }
  }

  getTaskSnapshotStat(state: string, organisationId?: number, users?: number[], context?: number[]): number {
    if (this.taskSnapshotStats.length > 0) {
      // if no users specified, set to empty array
      users = users || [];

      // declare variable to hold our filtered statistics
      let stats: Statistic[] = this.taskSnapshotStats;
      // use the context to filter down the stats we are interested in...
      if (context && context.length > 0) {
        switch (context[0]) {
          case -1: // filter by user
            stats = stats.filter((i) => users.includes(i.userId));
            break;
          case 0: // do nothing
            break;
          default:
            stats = stats.filter((i) => context.includes(i.userId));
            break;
        }
      }

      // if state specified then use it to filter list...
      // if no state specified then we assume need a count of tasks created...
      const s = state.toLowerCase();
      if (s === 'all') {
        // if state not specified then we need to remove items which are marked as 'created' or 'due'
        // this is so the tasks aren't counted twice as these states are not properly reflected in the task data
        stats = stats.filter((i) => (i.state.toLowerCase() !== 'created') && (i.state.toLowerCase() !== 'due'));
      }
      else {
        stats = stats.filter((i) => i.state.toLowerCase() === s);
      }

      if (this.userService.getCurrentUser().role.toLowerCase() == "multiorgadmin" && organisationId<1) {
          var orgList = this.userService.getCurrentUser().userOrganisations;
          stats = stats.filter((i) => orgList.findIndex(j=>j.organisationId===i.organisationId)>-1);
      }
      else {
        // if organisationId specified then use it to filter list...
        if (organisationId && (organisationId > 0)) {
          stats = stats.filter((i) => i.organisationId === organisationId);
        }
      }

      // now we have a filtered list of statistics to include, sum the counts to give us the value to return
      // + operator for casting to Number
      return stats.reduce((a, b) => +a + +b.count, 0);
    }
    else {
      return this.noStat();
    }
  }

  getTaskStat(state: string, from: string, to: string, users?: number[]) {
    if (this.taskStats.length > 0) {
      // if no users specified, set to empty array
      users = users || [];

      const fromDate = this.dateHelper.utc(from);
      const toDate = this.dateHelper.utc(to).add(1, 'seconds');

      // declare variable to hold our filtered statistics
      let stats: Statistic[];

      // if state specified then use it to filter list...
      if (state) {
        stats = this.taskStats.filter((i) => (users.indexOf(i.userId) !== -1) && (i.state.toLowerCase() === state.toLowerCase())
          && ((this.dateHelper.utc(i.date, 'YYYY-MM-DD') >= fromDate) && (this.dateHelper.utc(i.date, 'YYYY-MM-DD') < toDate)));
      }
      else {
        // if state not specified then we need to remove items which are marked as 'created' or 'due'
        // this is so the tasks aren't counted twice as these states are not properly reflected in the task data
        stats = this.taskStats.filter((i) => (users.indexOf(i.userId) !== -1) && ((i.state.toLowerCase() !== 'created') && (i.state.toLowerCase() !== 'due'))
          && ((this.dateHelper.utc(i.date, 'YYYY-MM-DD') >= fromDate) && (this.dateHelper.utc(i.date, 'YYYY-MM-DD') < toDate)));
      }

      // now we have a filtered list of statistics to include, sum the counts to give us the value to return
      // + operator for casting to Number
      return stats.reduce((a, b) => +a + +b.count, 0);
    }
    else {
      return this.noStat();
    }
  }

  getDateRangeForWeek(offsetWeeks?: number, referenceDate?: string): any[] {
    referenceDate = referenceDate || this.dateHelper.getDateAsString(undefined, 'DD/MMM/YYYY');
    let refDate = referenceDate;
    if (offsetWeeks && (offsetWeeks !== 0)) {
      refDate = this.dateHelper.utc(referenceDate, 'DD/MMM/YYYY').subtract((offsetWeeks * -1), 'weeks').format('DD/MMM/YYYY');
    }
    const startOfWeek = this.dateHelper.utc(refDate, 'DD/MMM/YYYY').startOf('week');
    const dates = [];
    let i: number;
    startOfWeek.subtract(1, 'days');
    for (i = 0; i < 7; i++) {
      const date = startOfWeek;
      date.add(1, 'days');
      dates.push({ key: date.format('ddd'), value: date.format('DD/MMM/YYYY') });
    }
    return dates;
  }

  getUserTaskStats(userUId: string) {
    this.getAll('user/' + userUId + '/task');
  }

  getUserTaskStatsData(userUId: string): Observable<Statistic[]> {
    return this.getAllData('user/' + userUId + '/task').pipe(take(1), tap((data) => {
      this.onChanged.next(this.applyDataFilter(data));
    }));
  }

  private noStat(): number {
    // Return random number for client side testing if required, else return zero
    // return Math.floor(Math.random() * 10) + 1;
    // console.log('No stat returned');
    return 0;
  }

  hydrate(item: Statistic): Observable<Statistic> {
    const hydrate$ = [];
    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        return item;
      }));
    }
    else {
      return of(item);
    }
  }

  getReferralSnapshotStat(subjectTypeId: number,
    users?: number[], context?: number[], organisationId: number = -1): number {

    if (this.referralSnapshotStats.length > 0) {

      const typePredicate = (x) => x.typeId === subjectTypeId;
      // if no users specified, set to empty array
      users = users || [];

      let predicate = typePredicate;

      const organisationPredicate = (x) => x.organisationId === organisationId;

      // get context predicate
      let contextPredicate;
      if (context && (context.length > 0)) {
        switch (context[0]) {
          case -2: // manager
            contextPredicate = (x) => users.includes(x.managerId);
            break;
          case -1: // filter by user
            contextPredicate = (x) => users.includes(x.userId);
            break;
          case 0:
            // do nothing
            break;
          default:
            contextPredicate = (x) => context.includes(x.userId);
            break;
        }
      }
      if (contextPredicate) {
        const prev = predicate;
        predicate = (x) => prev(x) && contextPredicate(x);
      }

      // if organisationId specified then use it to filter list...
      if (organisationId && (organisationId > 0)) {
        const prev = predicate;
        predicate = (x) => prev(x) && organisationPredicate(x);
      }

      // now we have a filtered list of statistics to include, sum the counts to give us the value to return
      // + operator for casting to Number
      return this.referralSnapshotStats.filter((x) => predicate(x)).reduce((a, b) => +a + +b.count, 0);
    }
    else {
      return this.noStat();
    }
  }

  getSubjectsDueReviewSnapshotStat(filterId?: number, isReferral?: boolean, users?: number[], context?: number[], organisationId: number = -1): number {
    if (this.subjectsDueReviewSnapshotStatsNew.length > 0) {
      var count = 0

      let contextPredicate = (x) => x.isUser === true;
      // contextPredicate = (x) => users.includes(x.userId);
      if (context && (context.length > 0)) {
        switch (context[0]) {
          case -2: // filter by manager
            contextPredicate = (x) => users.includes(x.managerId);
            break;
          case -1: // filter by user
            contextPredicate = (x) => users.includes(x.userId);
            break;
          case 0:
            contextPredicate = (x) => x.isUser === true;
            break;
          default:
            contextPredicate = (x) => x.userId === context[0];
            break;

        }
      }
      var propName = "";
      if (isReferral && isReferral == true) {
        switch (filterId) {
          case 1:
            propName = 'referralsDueInOneDay';
            break;
          case 7:
            propName = 'referralsDueInSevenDays';
            break;
          case 30:
            propName = 'referralsDueInThirtyDays';
            break;
          default:
            propName = 'allReferralsDue';
            break
        }
      } else {
        switch (filterId) {
          case 1:
            propName = 'casesDueInOneDay';
            break;
          case 7:
            propName = 'casesDueInSevenDays';
            break;
          case 30:
            propName = 'casesDueInThirtyDays';
            break;
          default:
            propName = 'allCasesDue';
            break;
        }
      }

      // if organisationId specified then use it to filter list...
      if (organisationId && (organisationId > 0)) {
        const organisationPredicate = (x) => x.organisationId === organisationId;
        const prev = contextPredicate;
        contextPredicate = (x) => prev(x) && organisationPredicate(x);
      }
      const filteredArray = this.subjectsDueReviewSnapshotStatsNew.filter((x) => contextPredicate(x));

      filteredArray.forEach((element) => {
        if (element[propName]) {
          count += element[propName] || 0;
        }
      });

      return count;
    } else {
      return this.noStat();
      // or if you want to combine into one line with reduce...
      // return this.subjectsDueReviewSnapshotStatsNew.filter((x) => contextPredicate(x)).reduce((a, b) => +a + +b[propName], 0);

    }
  }
}
export class SubjectsDueReviewDTO {
  isUser: boolean;
  userId: number;
  managerId: number;
  casesDueInOneDay: number;
  casesDueInSevenDays: number;
  casesDueInThirtyDays: number;
  allCasesDue: number;
  readonlyeferralsDueInOneDay: number;
  referralsDueInSevenDays: number;
  referralsDueInThirtyDays: number;
  allReferralsDue: number;
  organisationId: number;
}
